// People page tools for admins: inviting many people from a pasted list or a CSV file (checked first, then sent), and
// removing someone with their meeting links, recordings, teams and groups handed over to another member.
import { useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, type Role } from "../../lib/api";
import { Alert, Badge, Button, Dialog, Select, Spinner } from "../../components/ui";
import { useOrg } from ".";

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
type Row = { email: string; role?: Role };
type Result = { line: number; email: string; role: Role; status: string };
type Bulk = { dryRun: boolean; problem: string | null; results: Result[]; counts: Record<string, number> };

const STATUS: Record<string, { label: string; tone: "brand" | "slate" | "amber" | "red" }> = {
  invite: { label: "Will be invited", tone: "brand" }, invited: { label: "Invited", tone: "brand" },
  already_member: { label: "Already a member", tone: "slate" }, already_invited: { label: "Already invited", tone: "slate" },
  duplicate: { label: "Listed twice", tone: "slate" }, invalid_email: { label: "Not an email address", tone: "red" },
  role_not_allowed: { label: "You can't give this role", tone: "red" }, rate_limited: { label: "Daily limit reached, try tomorrow", tone: "amber" },
};
const ROLES = new Set(["member", "guest", "admin"]);

// One person per line: an email address, and optionally a role (member, guest, admin). A first line naming an "Email"
// column (and maybe "Role") is a header: those columns are used. Otherwise the cell with an @ is the address (or the
// first cell, so a mistyped one shows up as a problem instead of disappearing). Commas, semicolons and tabs separate.
export function parseList(text: string): Row[] {
  const split = (line: string) => line.split(/[,;\t]/).map((c) => c.trim().replace(/^"(.*)"$/, "$1").trim());
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  let emailCol = -1, roleCol = -1;
  const head = split(lines[0] ?? "");
  if (!head.some((c) => c.includes("@")) && head.some((c) => /^e-?mail( address)?$/i.test(c))) {
    emailCol = head.findIndex((c) => /^e-?mail( address)?$/i.test(c));
    roleCol = head.findIndex((c) => /^role$/i.test(c));
    lines.shift();
  }
  return lines.map((line) => {
    const cells = split(line);
    const email = emailCol >= 0 ? cells[emailCol] ?? "" : cells.find((c) => c.includes("@")) ?? cells[0] ?? "";
    const r = (roleCol >= 0 ? cells[roleCol] ?? "" : cells.find((c) => ROLES.has(c.toLowerCase())) ?? "").toLowerCase();
    return ROLES.has(r) ? { email, role: r as Role } : { email };
  });
}

export function BulkInviteDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { org, role } = useOrg();
  const [text, setText] = useState("");
  const [defaultRole, setDefaultRole] = useState<Role>("member");
  const [result, setResult] = useState<Bulk | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = parseList(text);
  function close() { setText(""); setResult(null); setError(null); onClose(); }
  async function run(dryRun: boolean) {
    setBusy(true); setError(null);
    try {
      const r = await api<Bulk>(`/orgs/${org.slug}/invitations/bulk`, { method: "POST", body: { rows, role: defaultRole, dryRun } });
      setResult(r);
      if (!dryRun) onDone();
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  async function file(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) { setError("That file is too big (1 MB at most)."); return; }
    setText(await f.text()); setResult(null);
  }
  const willInvite = result?.results.filter((r) => r.status === "invite").length ?? 0;
  const sent = result && !result.dryRun;
  return (
    <Dialog open={open} onClose={close} title="Invite many people">
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {!result && (
          <>
            <div>
              <label htmlFor="bulk-list" className="mb-1.5 block text-sm font-medium">Email addresses, one per line</label>
              <textarea id="bulk-list" value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder={"ana@company.com\nraj@company.com, guest\nlee@company.com; admin"}
                className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-900" />
              <p className="mt-1.5 text-sm text-slate-500">Or choose a CSV file (from Excel or Google Sheets): a column of emails, and optionally a role column (member, guest{role === "owner" ? ", admin" : ""}). Up to 300 people.</p>
              <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={file} aria-label="Choose a CSV file" className="mt-2 block text-sm" />
            </div>
            <Select label="Role for rows without one" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as Role)}>
              <option value="member">Member</option>
              <option value="guest">Guest (limited access)</option>
              {role === "owner" && <option value="admin">Admin</option>}
            </Select>
          </>
        )}
        {result && (
          <>
            {result.problem && <Alert>{result.problem} Nothing was sent.</Alert>}
            {sent && <Alert kind="success">{result.counts.invited ?? 0} invitation{result.counts.invited === 1 ? "" : "s"} sent.</Alert>}
            <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-800" aria-label="Invitation list">
              {result.results.map((r) => (
                <li key={r.line} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 truncate"><span className="text-slate-400">{r.line}.</span> {r.email || "(empty)"} <span className="text-slate-500">· {r.role}</span></span>
                  <Badge tone={STATUS[r.status]?.tone ?? "slate"}>{STATUS[r.status]?.label ?? r.status}</Badge>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="flex justify-end gap-2">
          {result && !sent && <Button type="button" variant="secondary" onClick={() => setResult(null)}>Edit the list</Button>}
          <Button type="button" variant="secondary" onClick={close}>{sent ? "Done" : "Cancel"}</Button>
          {!result && <Button type="button" onClick={() => run(true)} loading={busy} disabled={!rows.length}>Check {rows.length || ""} {rows.length === 1 ? "person" : "people"}</Button>}
          {result && !sent && !result.problem && <Button type="button" onClick={() => run(false)} loading={busy} disabled={!willInvite}>Send {willInvite} invitation{willInvite === 1 ? "" : "s"}</Button>}
        </div>
      </div>
    </Dialog>
  );
}

type Member = { id: string; name: string; status: string };

export function RemoveMemberDialog({ member, members, onClose, onDone }: { member: Member; members: Member[]; onClose: () => void; onDone: () => void }) {
  const { org, me } = useOrg();
  const summary = useQuery({ queryKey: ["handover", org.slug, member.id], queryFn: () => api<{ meetings: number; recordings: number; teams: number; groups: number }>(`/orgs/${org.slug}/members/${member.id}/handover`) });
  const others = members.filter((m) => m.id !== member.id && m.status === "active");
  const [to, setTo] = useState<string>(others.some((m) => m.id === me.user.id) ? me.user.id : others[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = summary.data;
  const items: [number, string][] = s ? ([
    [s.meetings, s.meetings === 1 ? "meeting link" : "meeting links"], [s.recordings, s.recordings === 1 ? "recording" : "recordings"],
    [s.teams, s.teams === 1 ? "team they own" : "teams they own"], [s.groups, s.groups === 1 ? "group chat or channel they made" : "group chats or channels they made"],
  ] as [number, string][]).filter(([n]) => n > 0) : [];
  async function remove() {
    setBusy(true); setError(null);
    try {
      await api(`/orgs/${org.slug}/members/${member.id}`, { method: "DELETE", body: to ? { transferTo: to } : {} });
      onDone(); onClose();
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <Dialog open onClose={onClose} title={`Remove ${member.name}`}>
      <div className="space-y-4 text-sm">
        {error && <Alert>{error}</Alert>}
        <p>{member.name} will lose access to {org.name}: its chats, calls and meetings.</p>
        {summary.isLoading ? <div className="flex justify-center text-brand-600"><Spinner /></div> : items.length > 0 ? (
          <>
            <div>
              <p className="font-medium">They look after:</p>
              <ul className="mt-1 list-disc pl-5 text-slate-600 dark:text-slate-300">
                {items.map(([n, label]) => <li key={label}>{n} {label}</li>)}
              </ul>
            </div>
            <Select label="Hand these over to" value={to} onChange={(e) => setTo(e.target.value)}>
              {others.map((m) => <option key={m.id} value={m.id}>{m.name}{m.id === me.user.id ? " (you)" : ""}</option>)}
              <option value="">Nobody - end their meeting links</option>
            </Select>
          </>
        ) : <p className="text-slate-500">They don't look after any meeting links, recordings, teams or groups.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={remove} loading={busy}>Remove</Button>
        </div>
      </div>
    </Dialog>
  );
}
